// app/UpdateWatchers.jsx — extracted from App.js (behaviour-preserving split).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { AppUpdateCard } from "@/components/AppUpdateCard";
import { toast } from "@/components/ui/sonner";
import { armPeriodicApkUpdateCheck, launchApkInstall, openInstallPermissionSettings, recordUserDeferredUpdate } from "@/lib/apkUpdater";
import { refreshSnapshotNow, snapshotFreshWithin } from "@/lib/snapshotRefresh";
import { applyPendingUpdate, armOtaUpdater, subscribeOtaReady } from "@/lib/otaUpdater";
import { armPeriodicExeUpdateCheck, installedExeVersion, openExeDownload, recordExeUpdateDeferred } from "@/lib/desktopUpdater";
import { getDeviceId, isInstalledAppEnvironment } from "@/lib/device";
import { readRoleSession } from "@/lib/authStorage";


// Native APK update watcher — polls /api/apk/latest every few hours
// and, when a newer release is available, shows the AppUpdateCard
// (bottom sheet on phones) that drives the whole install flow.
// IndexedDB / Preferences / offline auth survive the in-place upgrade.
//
// Session 30.38 — One-tap install via the native NfcsInstaller plugin
// (in-app download with progress → Android Install sheet). Browser
// download remains the fallback.
// Session 30.41 — Toast replaced by a proper update card with progress.
export function ApkUpdateWatcher() {
  const [update, setUpdate] = useState(null); // { meta, phase, percent, received, total, error, installedVersion }
  const busyRef = useRef(false);

  const runInstall = useCallback(async (meta) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setUpdate((u) => ({ ...(u || { meta }), meta, phase: "downloading", percent: -1, received: 0, total: 0, error: null }));
    let lastErr = null;
    let result = false;
    try {
      result = await launchApkInstall(meta.download_url, {
        sha256: meta.sha256 || "",
        onProgress: (ev) => {
          const pct = Number(ev?.percent);
          setUpdate((u) => (u ? { ...u, phase: "downloading", percent: Number.isFinite(pct) ? pct : -1, received: Number(ev?.received || 0), total: Number(ev?.total || 0) } : u));
        },
        onError: (err) => { lastErr = err; },
      });
    } catch (err) { lastErr = err; }
    busyRef.current = false;
    const msg = lastErr ? String(lastErr?.message || lastErr).slice(0, 140) : null;
    if (result === "installer") setUpdate((u) => (u ? { ...u, phase: "ready", error: null } : u));
    else if (result === "settings") setUpdate((u) => (u ? { ...u, phase: "permission", error: null } : u));
    else if (result === "browser") setUpdate((u) => (u ? { ...u, phase: "browser", error: msg } : u));
    else setUpdate((u) => (u ? { ...u, phase: "error", error: msg || "Please try again or download from the admin release page." } : u));
  }, []);

  // Debug hook (harmless): lets a tester trigger the card from the console
  // / automation with a fake meta object.
  useEffect(() => {
    try {
      if (typeof window !== "undefined") {
        window.__nfcsShowApkUpdate = (meta) => setUpdate({ meta, phase: "available", percent: -1, received: 0, total: 0, error: null, installedVersion: process.env.REACT_APP_VERSION || "" });
        window.__nfcsRunApkInstall = (meta) => runInstall(meta);
      }
    } catch (_) { /* noop */ }
  }, [runInstall]);

  useEffect(() => {
    const stop = armPeriodicApkUpdateCheck((meta) => {
      setUpdate((u) => (u && u.phase === "downloading" ? u : {
        meta, phase: "available", percent: -1, received: 0, total: 0, error: null,
        installedVersion: process.env.REACT_APP_VERSION || "",
      }));
    });
    return () => { try { stop(); } catch (_) { /* noop */ } };
  }, []);

  if (!update) return null;
  return (
    <AppUpdateCard
      state={update}
      onInstall={() => runInstall(update.meta)}
      onRetry={() => runInstall(update.meta)}
      onOpenSettings={() => { try { openInstallPermissionSettings(); } catch (_) { /* noop */ } }}
      onLater={() => { try { recordUserDeferredUpdate(); } catch (_) { /* noop */ } setUpdate(null); }}
      onDismiss={() => setUpdate(null)}
    />
  );
}


// Session 30.25 — Windows EXE (Tauri) update watcher. Polls
// /api/exe/latest and offers a one-tap download of the new installer
// when the server advertises a newer tagged release than this build.
export function DesktopUpdateWatcher() {
  useEffect(() => {
    const stop = armPeriodicExeUpdateCheck((meta) => {
      const label = meta.version_name ? `v${meta.version_name}` : "New version";
      toast(`Update available: ${label}`, {
        description: `You are on v${installedExeVersion()}. Download the new installer and run it — your local data is kept.`,
        duration: Infinity,
        action: {
          label: "Download",
          onClick: () => { try { openExeDownload(meta.download_url); } catch (_) { /* noop */ } },
        },
        cancel: {
          label: "Later",
          onClick: () => { try { recordExeUpdateDeferred(); } catch (_) { /* noop */ } },
        },
      });
    });
    return () => { try { stop(); } catch (_) { /* noop */ } };
  }, []);
  return null;
}


// Session 27.63 — CapGo self-hosted OTA update watcher.
//
// The Capacitor plugin `@capgo/capacitor-updater` polls
// `/api/updates/latest` on every app resume (see the config in
// capacitor.config.ts). When a new JS bundle has been downloaded and
// staged for the next launch, we surface a sticky toast asking the
// operator to tap "Restart to apply" — never yank the JS out from
// under a running shift.
//
// Difference vs ApkUpdateWatcher:
//   • APK updates require re-installing the whole native shell
//     (owner triggers on tablet).
//   • OTA updates only replace the JS/CSS bundle inside the SAME
//     installed APK — much smaller download, much faster to apply,
//     and does not require re-authorising installation on Android.
export function OtaUpdateWatcher() {
  useEffect(() => {
    let toastId = null;
    // Arm the plugin listeners once. Safe to call in non-native
    // contexts — armOtaUpdater short-circuits when not inside
    // Capacitor / Tauri.
    armOtaUpdater();
    const unsub = subscribeOtaReady(({ version }) => {
      // De-dupe: replace any existing toast so a rapid double-fire
      // doesn't stack two identical prompts on top of each other.
      if (toastId) { try { toast.dismiss(toastId); } catch (_) { /* noop */ } }
      const label = version ? `v${version}` : "New app update";
      toastId = toast(`${label} downloaded`, {
        description: "Tap Restart to apply the update. Your work is saved and this device will keep operating offline.",
        duration: Infinity,
        action: {
          label: "Restart",
          onClick: () => { applyPendingUpdate(); },
        },
        cancel: {
          label: "Later",
          onClick: () => { try { toast.dismiss(toastId); } catch (_) { /* noop */ } },
        },
      });
    });
    return () => {
      try { unsub(); } catch (_) { /* noop */ }
      if (toastId) try { toast.dismiss(toastId); } catch (_) { /* noop */ }
    };
  }, []);
  return null;
}


// Sticky toast shown when the service worker has a new version waiting.
// User opts in via the "Reload" action — we never auto-reload mid-service.
export function PwaUpdateToast() {
  useEffect(() => {
    let toastId = null;
    const onReady = () => {
      if (toastId) return;
      toastId = toast("New version available", {
        description: "Reload to install the latest update. Your work is saved.",
        duration: Infinity,
        action: {
          label: "Reload",
          onClick: () => {
            try { window.__nfcsUpdate && window.__nfcsUpdate.apply && window.__nfcsUpdate.apply(); }
            catch (_) { window.location.reload(); }
          },
        },
      });
    };
    window.addEventListener("nfcs:update-ready", onReady);
    if (window.__nfcsUpdate) onReady();
    return () => {
      window.removeEventListener("nfcs:update-ready", onReady);
      if (toastId) try { toast.dismiss(toastId); } catch (_) { /* noop */ }
    };
  }, []);
  return null;
}


/**
 * MorningUpdateSplash — auto-pull fresh business snapshot on the
 * first sign-in of each calendar day (Phase 3, Session 27.56+).
 *
 * On the coordinator or any leaf device, the first time we land on
 * an operational dashboard on a new calendar day we transparently
 * refresh the local snapshot from the server (menu, categories,
 * tags, sides, specials, settings, departments — the full ~38-
 * collection payload served by `/api/sync/snapshot`). This is what
 * lets an owner edit prices from the Owner dashboard at 22:00 and
 * have every tablet on the LAN wake up the next morning already
 * showing the new menu without anyone having to hit refresh.
 *
 * Rules (per the Session 27.53 spec — "silently update everything
 * automatically when you sign in"):
 *   • Never asks the user to confirm — just runs.
 *   • Shows a full-screen "Updating…" splash so the operator knows
 *     something's happening on a slow connection instead of seeing
 *     a blank / half-hydrated dashboard.
 *   • Runs AT MOST ONCE per calendar day per (business, device).
 *   • If the network is unreachable / the refresh fails, we abort
 *     the splash after 15 seconds and let the operator into the
 *     dashboard — the existing snapshot from yesterday remains
 *     usable, and the periodic snapshot loop will retry silently.
 *   • Only in installed contexts (APK / EXE). Browser tabs stay
 *     cloud-first and never see this splash.
 */
const MORNING_REFRESH_STORAGE_PREFIX = "nfcs:morning-refresh";
const MORNING_REFRESH_TIMEOUT_MS = 15000;

function _todayDateKey() {
  // Use the device's local date — we don't have the business timezone
  // client-side and this is only used for "have we already refreshed
  // today?" bookkeeping, not for any authoritative accounting.
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function _morningRefreshKey(businessId, deviceId, role) {
  const parts = [
    MORNING_REFRESH_STORAGE_PREFIX,
    businessId || "no-biz",
    deviceId || "no-dev",
    role || "no-role",
  ];
  return parts.join(":");
}

export function MorningUpdateSplash() {
  const location = useLocation();
  const [isInstalledCtx] = useState(() => {
    try { return isInstalledAppEnvironment(); } catch (_) { return false; }
  });
  const [deviceId] = useState(() => {
    try { return getDeviceId(); } catch (_) { return ""; }
  });

  // "refreshing" is the visible-splash state.
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const checkedForRouteRef = useRef(null);

  const dashboardRouteMatch = /^\/(staff|owner|kitchen|bar|runner|admin)\/dashboard/.test(location.pathname);
  const roleFromPath = dashboardRouteMatch ? location.pathname.split("/")[1] : null;

  const activeSession = useMemo(() => {
    if (!dashboardRouteMatch || !roleFromPath) return null;
    try {
      const parsed = readRoleSession(roleFromPath, "tab");
      return parsed && parsed.token ? parsed : null;
    } catch (_) { return null; }
  }, [dashboardRouteMatch, roleFromPath]);

  useEffect(() => {
    if (!isInstalledCtx) return undefined;
    if (!activeSession || !activeSession.token) return undefined;
    if (String(activeSession.token).startsWith("offline:")) return undefined;
    const routeKey = `${roleFromPath}:${activeSession.token.slice(0, 8)}`;
    if (checkedForRouteRef.current === routeKey) return undefined;
    checkedForRouteRef.current = routeKey;

    const businessId = activeSession?.business?.id || activeSession?.business_id || "";
    const storageKey = _morningRefreshKey(businessId, deviceId, roleFromPath);
    const today = _todayDateKey();
    let lastRun = null;
    try { lastRun = window.localStorage.getItem(storageKey); } catch (_) { /* noop */ }
    if (lastRun === today) return undefined;
    // Session 30.34 — data downloaded in the last 2 h (install wizard,
    // previous role's login, manual refresh) is fresh enough: skip the
    // refresh entirely instead of showing "Updating…" again.
    if (snapshotFreshWithin(2 * 60 * 60 * 1000)) {
      try { window.localStorage.setItem(storageKey, today); } catch (_) { /* noop */ }
      return undefined;
    }

    let cancelled = false;
    let timeoutId = null;
    setRefreshing(true);
    setError(null);
    (async () => {
      try {
        const withTimeout = new Promise((resolve, reject) => {
          timeoutId = setTimeout(() => reject(new Error("timeout")), MORNING_REFRESH_TIMEOUT_MS);
        });
        await Promise.race([refreshSnapshotNow(), withTimeout]);
        if (cancelled) return;
        try { window.localStorage.setItem(storageKey, today); } catch (_) { /* noop */ }
      } catch (err) {
        if (cancelled) return;
        setError((err && err.message) || "update-failed");
        try { window.localStorage.setItem(storageKey, today); } catch (_) { /* noop */ }
      } finally {
        if (!cancelled) {
          if (timeoutId) clearTimeout(timeoutId);
          setTimeout(() => { if (!cancelled) setRefreshing(false); }, 400);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isInstalledCtx, activeSession, deviceId, roleFromPath]);

  if (!refreshing) return null;
  // Session 30.34 — was a full-screen blocking splash. The dashboard is
  // already usable from the local snapshot, so this is now a small,
  // non-blocking pill: the refresh runs in the background.
  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-4 z-[300] flex justify-center px-4"
      data-testid="morning-update-splash"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="flex items-center gap-2 rounded-full border border-border bg-card/95 px-4 py-2 text-xs shadow-lg backdrop-blur">
        <RefreshCw className="h-3.5 w-3.5 animate-spin text-[hsl(var(--primary))]" />
        <span className="font-medium" data-testid="morning-update-splash-title">
          {error ? "Using saved data" : "Refreshing menu & settings…"}
        </span>
      </div>
    </div>
  );
}

// DevTools helper — lets the operator force a morning refresh without
// waiting for the date to roll over. Useful when the owner has just
// pushed a menu edit and wants the tablet to pick it up right now, or
// when we're testing this codepath in preview. Exposed on `window` so
// it can be run from the browser console: `window.nfcsForceMorningRefresh()`.
if (typeof window !== "undefined") {
  try {
    window.nfcsClearMorningRefresh = () => {
      try {
        Object.keys(window.localStorage)
          .filter((k) => k.startsWith(MORNING_REFRESH_STORAGE_PREFIX))
          .forEach((k) => window.localStorage.removeItem(k));
        // eslint-disable-next-line no-console
        console.log("[nfcsClearMorningRefresh] cleared — reload the dashboard to trigger the splash.");
      } catch (_) { /* noop */ }
    };
  } catch (_) { /* noop */ }
}
